import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { List, Ticket, UserProfile } from '@/lib/types'
import { TicketCard } from './TicketCard'
import type { AudioSpeed } from './InlineAudio'
import { useConfirm } from './ConfirmDialog'
import { cn, displayListName, listDotClass, LIST_COLORS } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { sanitizeRichHtml } from '@/lib/richtext'
import i18n from '@/i18n'

/** Small inline escape — used to wrap plain-text pastes into a safe HTML
 *  paragraph when the clipboard didn't carry an HTML payload. */
function escapeHtmlText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\n/g, '<br>')
}
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Plus,
  X,
  GripVertical,
  ArrowDownWideNarrow,
  Rows3,
  StretchHorizontal,
  Check,
} from 'lucide-react'

interface Props {
  list: List
  /** All lists of the project — passed to the card menu's « move to
   *  another list » picker. */
  allLists?: List[]
  tickets: Ticket[]
  searchActive?: boolean
  profilesById: Record<string, UserProfile>
  exitingIds: Set<string>
  /** Cards mid entrance animation — fresh inserts, locally or via realtime. */
  enteringIds: Set<string>
  /** Ids of cards currently flagged "new" for this user. */
  newTicketIds: Set<string>
  /** True while a card is dragged over this column — highlights the dropzone. */
  isOver?: boolean
  /** When set, render a placeholder slot at this index in the cards list
   *  — only used during a CROSS-LIST drag to indicate where the incoming
   *  card will land. `null` = no placeholder (= same-list drag, or this
   *  column isn't the current destination). */
  dropPlaceholderIndex?: number | null
  /** The id of the card currently being dragged anywhere on the board.
   *  Used to identify which card in THIS column is the source so it can
   *  be hidden when the drag has moved to a different list. */
  activeDragTicketId?: string | null
  /** True when the current drag has crossed into a DIFFERENT list. The
   *  source card uses this to collapse itself (h-0) so the "ghost"
   *  visually follows the cursor across columns. False while still in
   *  the source list (the card stays as a translucent ghost in place). */
  crossListActive?: boolean
  /** Id of the static target card while a merge is charging (~2s dwell). The
   *  matching card in this column vibrates + grows a yellow halo. */
  mergeTargetId?: string | null
  /** Where a manually-added card lands (user pref). 'top' renders the
   *  composer above the cards (and the new card lands on top); 'bottom'
   *  renders it below (Trello-style append). */
  newCardPosition?: 'top' | 'bottom'
  /** Deduplicated labels used anywhere in this project — surfaced as one-
   *  click suggestion chips in each card's contextual menu. */
  projectLabels?: string[]
  /** Spread onto the column's drag handle (grip) for list reordering. */
  dragHandleProps?: Record<string, unknown>
  onTicketClick?: (t: Ticket) => void
  /** True when the board is the owner's central Inbox (cards show a
   *  prominent « Affecter à un canal » button). */
  isInbox?: boolean
  /** Open the « Déplacer la carte » dialog (cross-project move). */
  onMoveCard?: (t: Ticket) => void
  /** Open the « Répondre » dialog (AI-suggested replies). */
  onReplyCard?: (t: Ticket) => void
  onViewMedia?: (urls: string[]) => void
  onViewAudio?: (urls: string[], ticket: Ticket) => void
  /** Transcribe one of a card's audios — rewrites the card's title +
   *  description from the result (inline audio player's kebab menu). */
  onTranscribeAudio?: (
    ticket: Ticket,
    audioUrl: string,
  ) => void | Promise<void>
  /** Tickets currently being transcribed (card-level). Each matching
   *  card shows a small « Transcription en cours… » chip. */
  transcribingTicketIds?: Set<string>
  /** Global audio playback rate (1×, 1.25×, …, 2×) and its setter. */
  audioSpeed?: AudioSpeed
  onAudioSpeedChange?: (rate: AudioSpeed) => void
  onHoverChange?: (t: Ticket | null, rect: DOMRect | null) => void
  onArchiveCard?: (id: string) => void
  /** Permanently delete a card (kebab « Supprimer », undo-toast deferred). */
  onDeleteCard?: (id: string) => void
  onMarkSeen?: (id: string) => void
  onUpdateTicket?: (id: string, patch: Partial<Ticket>) => void | Promise<void>
  /** Whether THIS list renders its cards in slim layout. Toggled by the
   *  kebab-menu item below; persisted per-user via App's preferences. */
  compact?: boolean
  onToggleCompact?: () => void
  subscribableUserIds?: string[]
  subsByTicket?: Map<string, Set<string>>
  currentUserId?: string
  onToggleSubscriber?: (
    ticketId: string,
    userId: string,
    subscribe: boolean,
  ) => void | Promise<void>
  onCreateCard: (
    listId: string,
    title: string,
    description?: string,
  ) => void | Promise<void>
  onRename: (id: string, name: string) => void | Promise<void>
  onDelete: (id: string) => void | Promise<void>
  onSetColor: (id: string, color: string | null) => void | Promise<void>
}

export function Column({
  list,
  allLists,
  tickets,
  searchActive = false,
  profilesById,
  exitingIds,
  enteringIds,
  newTicketIds,
  isOver = false,
  dropPlaceholderIndex = null,
  activeDragTicketId = null,
  crossListActive = false,
  mergeTargetId = null,
  newCardPosition = 'top',
  projectLabels,
  dragHandleProps,
  onTicketClick,
  isInbox = false,
  onMoveCard,
  onReplyCard,
  onViewMedia,
  onViewAudio,
  onTranscribeAudio,
  transcribingTicketIds,
  audioSpeed,
  onAudioSpeedChange,
  onHoverChange,
  onArchiveCard,
  onDeleteCard,
  onMarkSeen,
  onUpdateTicket,
  compact,
  onToggleCompact,
  subscribableUserIds,
  subsByTicket,
  currentUserId,
  onToggleSubscriber,
  onCreateCard,
  onRename,
  onDelete,
  onSetColor,
}: Props) {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(() => displayListName(list))
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [composing, setComposing] = useState(false)
  const [cardDraft, setCardDraft] = useState('')
  const composerRef = useRef<HTMLTextAreaElement | null>(null)
  // When the user pastes a long block of text into the title field, we run
  // it through summarize-paste: the AI returns a short title (which prefills
  // the input) and we stage the FULL pasted HTML as the description, so
  // hitting « Ajouter » creates a card with both. Keeps the formatting.
  const [pendingDescription, setPendingDescription] = useState<string | null>(
    null,
  )
  const [pendingPreview, setPendingPreview] = useState<string>('')
  const [summarizing, setSummarizing] = useState(false)
  const confirm = useConfirm()
  // Local view option: sort cards by date (recent → old) instead of manual order.
  const [sortByDate, setSortByDate] = useState(false)

  const displayTickets = useMemo(() => {
    if (!sortByDate) return tickets
    return [...tickets].sort((a, b) =>
      (b.source_date ?? b.created_at).localeCompare(a.source_date ?? a.created_at),
    )
  }, [tickets, sortByDate])

  useEffect(() => {
    if (composing && composerRef.current) composerRef.current.focus()
  }, [composing])

  function submitCard() {
    const t = cardDraft.trim()
    if (t) onCreateCard(list.id, t, pendingDescription ?? undefined)
    setCardDraft('')
    setPendingDescription(null)
    setPendingPreview('')
    // Keep the composer open so several cards can be added in a row (Trello-style).
    composerRef.current?.focus()
  }

  /** Intercept paste in the title field: when the user drops a "big" chunk
   *  of text in, we don't want to spam the title input with it. Instead we
   *  stage the FULL pasted content (HTML if available, so colors / bold /
   *  highlights survive) as the description, and ask Claude for a short
   *  title + summary that prefill the input. Small pastes flow through
   *  untouched so quick title typing isn't slowed down. */
  async function handleComposerPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const html = e.clipboardData.getData('text/html')
    const plain = e.clipboardData.getData('text/plain')
    const text = (plain || '').replace(/\r\n/g, '\n')
    // Heuristic: "big" = ≥80 chars OR contains a newline. Anything smaller
    // is probably just a title fragment — let it paste normally.
    const isBigPaste = text.length >= 80 || /\n/.test(text)
    if (!isBigPaste) return
    e.preventDefault()
    // Stage the HTML description right away so the formatting is locked in
    // even if the AI call is slow / fails.
    const htmlPart = html ? sanitizeRichHtml(html) : null
    const stagedHtml = htmlPart || `<p>${escapeHtmlText(text)}</p>`
    setPendingDescription(stagedHtml)
    const previewWordCount = text.trim().split(/\s+/).filter(Boolean).length
    setPendingPreview(text.trim().slice(0, 120))
    // Show a placeholder title while the AI runs so the user has feedback.
    if (!cardDraft.trim()) setCardDraft(t('list.composerSummarizing'))
    setSummarizing(true)
    try {
      const { data, error } = await supabase.functions.invoke(
        'summarize-paste',
        { body: { text, locale: i18n.language } },
      )
      if (error) throw new Error(error.message)
      const result = data as { ok?: boolean; title?: string; summary?: string }
      const aiTitle = (result.title ?? '').trim()
      const aiSummary = (result.summary ?? '').trim()
      // Prefill the title input (replacing the « Génération… » placeholder).
      if (aiTitle) setCardDraft(aiTitle)
      else if (cardDraft === t('list.composerSummarizing')) setCardDraft('')
      // Prepend the summary as a short lead paragraph in italics ahead of
      // the full pasted content — gives the card a clean intro line.
      if (aiSummary) {
        const lead = `<p><em>${escapeHtmlText(aiSummary)}</em></p>`
        setPendingDescription((cur) => (cur ? `${lead}${cur}` : `${lead}${stagedHtml}`))
      }
      // Update the small preview chip with the word count + a snippet.
      setPendingPreview(
        `${previewWordCount} ${t(
          previewWordCount === 1 ? 'list.wordOne' : 'list.wordOther',
        )} · ${text.trim().slice(0, 80)}…`,
      )
    } catch (err) {
      console.error('summarize-paste failed', err)
      // AI down: keep the staged HTML, drop the placeholder title so the
      // user can type one manually.
      if (cardDraft === t('list.composerSummarizing')) setCardDraft('')
    } finally {
      setSummarizing(false)
      composerRef.current?.focus()
    }
  }

  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [renaming])

  useEffect(() => {
    if (!menuOpen) return
    const close = () => setMenuOpen(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menuOpen])

  function commitRename() {
    // If the user opened the rename input and submitted without typing
    // anything, draft === displayListName(list). For a default list
    // (Reçu/En cours/Terminé) the displayed value is the translated
    // string, which is NOT equal to the stored French value. Without
    // this check we'd overwrite the DB with the translation, breaking
    // the language-switch behavior for that list.
    if (draft === displayListName(list)) {
      setRenaming(false)
      return
    }
    onRename(list.id, draft)
    setRenaming(false)
  }

  async function confirmDelete() {
    setMenuOpen(false)
    const ok = await confirm({
      title: t('list.confirmDeleteTitle', { name: displayListName(list) }),
      message: t('list.confirmDeleteMessage', { count: tickets.length }),
      confirmLabel: t('list.confirmDeleteButton'),
    })
    if (ok) onDelete(list.id)
  }

  // Manual card composer (hidden while a search filter is active). Rendered
  // either above the cards (new_card_position 'top') or below them
  // ('bottom') so the input sits exactly where the new card will land.
  const composerBlock = !searchActive && (
    composing ? (
      <div className="rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-2 shadow-[var(--shadow-soft)]">
        <textarea
          ref={composerRef}
          value={cardDraft}
          onChange={(e) => setCardDraft(e.target.value)}
          onPaste={handleComposerPaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submitCard()
            }
            if (e.key === 'Escape') {
              setCardDraft('')
              setPendingDescription(null)
              setPendingPreview('')
              setComposing(false)
            }
          }}
          rows={2}
          placeholder={t('list.cardTitlePlaceholder')}
          className="w-full resize-none rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-[12.5px] text-[var(--color-text)] outline-none focus:border-[var(--color-link)]"
        />
        {(pendingDescription || summarizing) && (
          <div className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/60 px-2 py-1.5 text-[11px] text-[var(--color-text)]">
            <span className="mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)] text-white">
              {summarizing ? (
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-white" />
              ) : (
                '✓'
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="font-semibold">
                {summarizing
                  ? t('list.composerSummarizing')
                  : t('list.composerDescriptionReady')}
              </span>
              {pendingPreview && (
                <span className="ml-1 text-[var(--color-text-dim)]">
                  {pendingPreview}
                </span>
              )}
            </span>
            {pendingDescription && !summarizing && (
              <button
                type="button"
                onClick={() => {
                  setPendingDescription(null)
                  setPendingPreview('')
                }}
                className="shrink-0 rounded p-0.5 text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
                aria-label={t('common.close')}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
        <div className="mt-1.5 flex items-center gap-1.5">
          <button
            type="button"
            onClick={submitCard}
            disabled={summarizing}
            className="rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t('list.addCardButton')}
          </button>
          <button
            type="button"
            onClick={() => {
              setCardDraft('')
              setPendingDescription(null)
              setPendingPreview('')
              setComposing(false)
            }}
            className="rounded-lg p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)]"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => setComposing(true)}
        className="flex w-full items-center gap-1.5 rounded-xl px-2.5 py-2 text-[12.5px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
      >
        <Plus className="h-4 w-4" />
        {t('list.addCard')}
      </button>
    )
  )

  return (
    <div className="flex min-h-0 w-full flex-col rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] shadow-[var(--shadow-soft)]">
      {/* Header */}
      <div className="relative flex shrink-0 items-center justify-between px-4 pt-3.5 pb-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span
            {...dragHandleProps}
            title={t('list.dragToReorder')}
            aria-label={t('list.reorderAria')}
            className="-ml-1 shrink-0 cursor-grab touch-none rounded p-0.5 text-[var(--color-text-dim)] opacity-40 transition-opacity hover:opacity-100 active:cursor-grabbing"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </span>
          <span className={cn('h-2 w-2 shrink-0 rounded-full', listDotClass(list))} />
          {renaming ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename()
                if (e.key === 'Escape') {
                  setDraft(displayListName(list))
                  setRenaming(false)
                }
              }}
              className="min-w-0 flex-1 rounded-md border border-[var(--color-link)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[13px] font-semibold text-[var(--color-text-strong)] outline-none"
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setDraft(displayListName(list))
                setRenaming(true)
              }}
              className="truncate rounded px-1 py-0.5 -mx-1 text-left text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text-strong)] hover:bg-[var(--color-surface-3)]"
              title={t('list.renameAria')}
            >
              {displayListName(list)}
            </button>
          )}
          <span className="shrink-0 text-[12px] text-[var(--color-text-dim)] tabular-nums">
            {tickets.length}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => setSortByDate((v) => !v)}
            title={sortByDate ? t('list.sortByDateOn') : t('list.sortByDateOff')}
            aria-label={t('list.sortByDateAria')}
            className={cn(
              'rounded-lg p-1 transition-colors',
              sortByDate
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
            )}
          >
            <ArrowDownWideNarrow className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen((v) => !v)
            }}
            aria-label={t('list.options')}
            className="rounded-lg p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>

        {menuOpen && (
          <div
            className="absolute right-3 top-[calc(100%-2px)] z-20 w-52 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-[var(--shadow-modal)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Quick add — saves scrolling to the bottom composer on long
                lists. Opens the composer and brings it into view. */}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false)
                setComposing(true)
                window.setTimeout(() => {
                  composerRef.current?.focus()
                  composerRef.current?.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                  })
                }, 30)
              }}
              className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[12.5px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
            >
              <Plus className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
              {t('list.addCard')}
            </button>

            <div className="my-1 border-t border-[var(--color-border-soft)]" />

            <button
              type="button"
              onClick={() => {
                setMenuOpen(false)
                setDraft(displayListName(list))
                setRenaming(true)
              }}
              className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[12.5px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
            >
              <Pencil className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
              {t('list.rename')}
            </button>

            {onToggleCompact && (
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  onToggleCompact()
                }}
                className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[12.5px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
              >
                {compact ? (
                  <StretchHorizontal className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
                ) : (
                  <Rows3 className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
                )}
                {compact ? t('list.viewDetailed') : t('list.viewCompact')}
              </button>
            )}

            <div className="my-1 border-t border-[var(--color-border-soft)]" />
            {/* 12-colour pastel rainbow as a 6×2 grid. Lists use the
                saturated `dot` (matches the small dot rendered next to
                the list title in the header). Selected swatch shows a
                white check in-place — no ring/offset, so neighbours
                never visually rub against each other. */}
            <div className="grid grid-cols-6 gap-1.5 px-3 py-2">
              {LIST_COLORS.map((c) => {
                const selected = list.color === c.key
                return (
                  <button
                    key={c.key}
                    type="button"
                    title={c.label}
                    aria-pressed={selected}
                    onClick={() => {
                      setMenuOpen(false)
                      onSetColor(list.id, selected ? null : c.key)
                    }}
                    className={cn(
                      'relative inline-flex h-5 w-5 items-center justify-center rounded-full text-white transition-transform hover:z-10 hover:scale-110',
                      c.dot,
                    )}
                  >
                    {selected && (
                      <Check className="h-3 w-3" strokeWidth={3} />
                    )}
                  </button>
                )
              })}
            </div>

            {/* Delete — intake lists are unique to a project so we just hide
                this row for them (no need to explain why). */}
            {!list.is_intake && (
              <>
              <div className="my-1 border-t border-[var(--color-border-soft)]" />
              <button
                type="button"
                onClick={confirmDelete}
                className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[12.5px] text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-bg)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t('list.delete')}
              </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Body — dropzone */}
      <div
        // Tagged so KanbanBoard.handleDragOver can scope its live rect
        // queries to THIS column's cards (for cross-list drop placement).
        data-list-body={list.id}
        className={cn(
          // `pt-1.5` so the first card's top edge + its tinted shadow
          // breathe under the header instead of bleeding into it.
          'flex-1 space-y-2.5 overflow-y-auto px-3 pt-1.5 pb-3 transition-colors duration-150',
          isOver && 'bg-[var(--color-accent-bg)]/50',
        )}
      >
        {/* Composer at the TOP when the user's pref places new cards on top. */}
        {newCardPosition === 'top' && composerBlock}
        <SortableContext
          items={displayTickets.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {displayTickets.map((t, i) => (
            <Fragment key={t.id}>
              {dropPlaceholderIndex === i && <DropPlaceholder />}
              <TicketCard
                ticket={t}
                allLists={allLists}
                highlighted={searchActive}
                exiting={exitingIds.has(t.id)}
                entering={enteringIds.has(t.id)}
                isNew={newTicketIds.has(t.id)}
                assigneeProfile={t.assignee_id ? profilesById[t.assignee_id] : undefined}
                profilesById={profilesById}
                onClick={() => onTicketClick?.(t)}
                isInbox={isInbox}
                onMove={onMoveCard ? () => onMoveCard(t) : undefined}
                onReply={onReplyCard ? () => onReplyCard(t) : undefined}
                onViewMedia={onViewMedia}
                onViewAudio={onViewAudio}
                onTranscribeAudio={onTranscribeAudio}
                isTranscribing={transcribingTicketIds?.has(t.id) ?? false}
                audioSpeed={audioSpeed}
                onAudioSpeedChange={onAudioSpeedChange}
                onHoverChange={onHoverChange}
                onArchive={onArchiveCard}
                onDelete={onDeleteCard}
                onMarkSeen={onMarkSeen}
                onUpdate={onUpdateTicket}
                compact={compact}
                subscribableUserIds={subscribableUserIds}
                subscriberIds={subsByTicket?.get(t.id) ?? null}
                currentUserId={currentUserId}
                onToggleSubscriber={onToggleSubscriber}
                hideAsCrossListSource={
                  crossListActive && t.id === activeDragTicketId
                }
                merging={t.id === mergeTargetId}
                projectLabels={projectLabels}
              />
            </Fragment>
          ))}
          {dropPlaceholderIndex !== null &&
            dropPlaceholderIndex >= displayTickets.length && <DropPlaceholder />}
        </SortableContext>
        {tickets.length === 0 && dropPlaceholderIndex === null && (
          <div
            className={cn(
              'mt-1 rounded-2xl border border-dashed border-[var(--color-border)] py-8 text-center text-[11.5px] text-[var(--color-text-dim)] transition-colors',
              isOver &&
                'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]',
            )}
          >
            {isOver
              ? t('list.dropHere')
              : searchActive
                ? t('list.noResults')
                : t('list.empty')}
          </div>
        )}

        {/* Composer at the BOTTOM (default / Trello-style append). */}
        {newCardPosition !== 'top' && composerBlock}
      </div>
    </div>
  )
}

/** Ghost slot rendered in a DESTINATION column during a cross-list drag.
 *  Just a styled div in the DOM flow — NOT inside SortableContext.items
 *  — so it can't trigger the dnd-kit measurement loop that the previous
 *  override approach did (React #185). Cards below shift down naturally
 *  via the column body's `space-y-2.5`. Sized like a typical card so the
 *  visual "make room" effect is convincing. */
function DropPlaceholder() {
  return (
    <div
      aria-hidden="true"
      className="h-16 rounded-2xl border border-dashed border-[var(--color-accent)]/40 bg-[var(--color-accent-bg)]/30"
    />
  )
}

